/**
 * RelevanceEditToggle — `Edit Relevance` ↔ `Done` button shared
 * across every Child TARAs table. Toggles the per-table local
 * `editing` state so the relevance checkbox + justification input
 * flip between read-only display and live inputs.
 *
 * Mirrors the legacy `ChildAssetsSection.jsx` pattern (line 208-215):
 * one button, two labels, no separate Cancel.
 *
 * Story 15.F — clicking **Done** now persists modifications to the
 * backend via `approveHierarchicalData` (driven by the caller — this
 * primitive only renders the in-flight state). While the parent's
 * save callback is in flight the label flips to "Saving…" and the
 * button disables. Matches the legacy two-button contract:
 * `approveHierarchicalData` only saves, never advances the workflow
 * (that's the global Approve button's job).
 */
import React from 'react';
import { Icon } from '../../../components/ui/Icon';
import styles from './RelevanceEditToggle.module.css';

export interface RelevanceEditToggleProps {
  editing: boolean;
  onToggle: () => void;
  /** Disable the button (e.g. while the global approve is in-flight). */
  disabled?: boolean;
  /** True while the caller's save callback is in flight. Renders
   *  "Saving…" + disables the button so the user can't double-click. */
  pending?: boolean;
}

export default function RelevanceEditToggle({
  editing,
  onToggle,
  disabled,
  pending,
}: RelevanceEditToggleProps) {
  const label = pending ? 'Saving…' : editing ? 'Done' : 'Edit Relevance';
  return (
    <button
      type="button"
      className={`${styles.toggle} ${editing ? styles.toggleActive : ''}`}
      onClick={onToggle}
      disabled={disabled || pending}
      aria-pressed={editing}
      aria-busy={pending || undefined}
    >
      {editing ? <Icon name="check" size={14} /> : <Icon name="edit" size={14} />}
      {label}
    </button>
  );
}
